import { useSensors, useSensor, PointerSensor, KeyboardSensor, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { useState, useEffect, useRef } from 'react';
import { IRepresentativeProfilesTalentLevelResumeCategories } from 'shared/interfaces/IRepresentativesEditProfile';
import { profileService } from 'shared/services/profileService';
import { useDebouncedCallback } from 'use-debounce';
import { useSnackbar } from 'notistack';
import { AgencyPermissionEnums } from 'shared/enums/AgencyPermissionEnums';
import { useEditProfileMedia } from '../context/EditProfileMediaProvider';
import { useQueryClient } from 'react-query';

const { updateProfile } = profileService();

const err_msg = 'An error occurred while processing your request. Your previous sorting has been restored.';

export const useEditProfileMediaResumeDnd = (
  profileId: string,
  attachedResume: IRepresentativeProfilesTalentLevelResumeCategories[],
) => {
  const queryClient = useQueryClient();
  const { handleAction } = useEditProfileMedia();
  const [activeId, setActiveId] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: updateProfileMutateAsync } = updateProfile();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const prevData = useRef<IRepresentativeProfilesTalentLevelResumeCategories[]>([]);

  const [attachedItems, setAttachedItems] = useState<IRepresentativeProfilesTalentLevelResumeCategories[]>([]);

  const DebounceSort = useDebouncedCallback(() => {
    const updatedResumeSort = attachedItems?.map((resume) => resume.category_id);
    return updateProfileMutateAsync(
      {
        profileId,
        payload: {
          resume: updatedResumeSort,
        },
      },
      {
        onError: () => {
          setAttachedItems(prevData.current);
          enqueueSnackbar(err_msg, {
            variant: 'error',
          });
        },
        onSettled: () => {
          queryClient.resetQueries(['id', profileId]);

          prevData.current = [];
        },
      },
    );
  }, 1000);

  const handleOnDragStart = (event: DragEndEvent) => {
    DebounceSort.cancel();
    const { active } = event;
    if (!active) return;

    setActiveId(attachedItems?.findIndex((data) => data?.category_id === active.id));
  };

  const handleOnDragEnd = (event: DragEndEvent) => {
    handleAction(AgencyPermissionEnums.RESUME_CATEGORIES, () => {
      setActiveId(null);
      const { active, over } = event;
      if (!active || !over) return;
      if (active.id !== over.id) {
        const oldAttachedItem = attachedItems;
        prevData.current = oldAttachedItem;
        setAttachedItems((prev) => {
          const oldIndex = prev.findIndex((i) => active.id === i.category_id);
          const newIndex = prev.findIndex((i) => over.id === i.category_id);
          const latest = arrayMove(prev, oldIndex, newIndex);
          return latest;
        });
        DebounceSort();
      }
    });
  };

  useEffect(() => {
    if (attachedResume) {
      setAttachedItems(attachedResume);
    }
  }, [attachedResume]);

  const DndProps = {
    sensors,
    collisionDetection: closestCenter,
    onDragStart: handleOnDragStart,
    onDragEnd: handleOnDragEnd,
  };

  return { attachedItems, activeId, DndProps };
};
